import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useRecoilValue, useRecoilState } from 'recoil';
import { cn } from '@/lib/utils';
import { ComponentType } from '@/state/dragDrop';
import {
  visibleComponentsCountSelector,
  maximizedComponentState
} from '@/state/layout';
import { layoutPresetState } from '@/state/layoutPreset';
import {
  Maximize2,
  Minimize2,
  MessageSquare,
  Network,
  Terminal as TerminalIcon,
  GripVertical
} from 'lucide-react';

interface DraggableComponentProps {
  componentType: ComponentType;
  sourceZone: string;
  children: React.ReactNode;
  className?: string;
  tabExtras?: React.ReactNode;
}

const COMPONENT_META: Record<
  ComponentType,
  { label: string; icon: typeof MessageSquare }
> = {
  chat: { label: 'Chat', icon: MessageSquare },
  graph: { label: 'Graph', icon: Network },
  terminal: { label: 'Terminal', icon: TerminalIcon }
};

/**
 * Wraps a panel with a VS Code-style tab strip that shows the panel name and
 * an always-visible drag grip. The tab is the drag handle for the classic
 * drag-drop layout. Double-clicking the tab strip toggles a near-fullscreen
 * maximized view with a smooth growth/shrink animation.
 */
export const DraggableComponent: React.FC<DraggableComponentProps> = ({
  componentType,
  sourceZone,
  children,
  className,
  tabExtras
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);
  const visibleCount = useRecoilValue(visibleComponentsCountSelector);
  const preset = useRecoilValue(layoutPresetState);
  const [maximized, setMaximized] = useRecoilState(maximizedComponentState);
  const meta = COMPONENT_META[componentType];
  const Icon = meta.icon;

  const canDrag = visibleCount > 1 && preset === 'classic' && maximized === null;
  const isMaximized = maximized === componentType;
  const isOtherMaximized = maximized !== null && !isMaximized;

  const toggleMaximize = useCallback(() => {
    setMaximized((prev) => (prev === componentType ? null : componentType));
  }, [componentType, setMaximized]);

  useEffect(() => {
    if (!isMaximized) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMaximized(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMaximized, setMaximized]);

  const handleDragStart = (event: React.DragEvent) => {
    if (!canDrag) {
      event.preventDefault();
      return;
    }
    setIsDragging(true);
    const dragData = {
      componentType,
      sourceZone,
      offset: { x: 0, y: 0 }
    };
    event.dataTransfer.setData('application/vnd.crimson-raven.component', JSON.stringify(dragData));
    event.dataTransfer.effectAllowed = 'move';
    if (dragRef.current) {
      event.dataTransfer.setDragImage(dragRef.current, 0, 0);
    }
  };

  const handleDragEnd = () => setIsDragging(false);

  const handleHeaderDoubleClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('[data-no-maximize]')) return;
    toggleMaximize();
  };

  return (
    <>
      {isMaximized && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-40 bg-background/70 backdrop-blur-sm"
          onClick={() => setMaximized(null)}
        />
      )}
      <motion.div
        ref={dragRef}
        data-component-type={componentType}
        layout
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        data-source-zone={sourceZone}
        className={cn(
          'relative group flex flex-col min-h-0 rounded-md overflow-hidden border border-border bg-card transition-opacity duration-200',
          isDragging && 'opacity-50',
          isMaximized
            ? 'fixed inset-4 z-50 shadow-2xl'
            : 'h-full w-full',
          isOtherMaximized && 'opacity-60',
          className
        )}
      >
        <div
          draggable={canDrag}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDoubleClick={handleHeaderDoubleClick}
          title={
            canDrag
              ? `Drag to rearrange ${meta.label} · double-click to maximize`
              : `${meta.label} · double-click to ${isMaximized ? 'restore' : 'maximize'}`
          }
          className={cn(
            'flex h-6 shrink-0 items-center justify-between gap-2 px-2 select-none',
            'border-b border-border bg-muted/60',
            canDrag
              ? 'cursor-grab active:cursor-grabbing hover:bg-accent'
              : 'cursor-pointer hover:bg-accent/80'
          )}
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <Icon className="h-3 w-3 text-primary shrink-0" />
            <span className="text-[11px] font-medium tracking-wide uppercase text-muted-foreground truncate">
              {meta.label}
            </span>
          </div>
          <div className="flex items-center gap-2 min-w-0">
            {tabExtras ? (
              <div
                data-no-maximize
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                draggable={false}
                className="flex items-center cursor-default"
              >
                {tabExtras}
              </div>
            ) : null}
            <button
              type="button"
              data-no-maximize
              onClick={(e) => {
                e.stopPropagation();
                toggleMaximize();
              }}
              onDoubleClick={(e) => e.stopPropagation()}
              className="flex h-4 w-4 items-center justify-center rounded text-muted-foreground/70 hover:text-foreground hover:bg-accent/80 transition-colors"
              title={isMaximized ? 'Restore' : 'Maximize'}
              aria-label={isMaximized ? 'Restore panel' : 'Maximize panel'}
            >
              {isMaximized ? (
                <Minimize2 className="h-3 w-3" aria-hidden="true" />
              ) : (
                <Maximize2 className="h-3 w-3" aria-hidden="true" />
              )}
            </button>
            {canDrag && (
              <GripVertical
                className="h-3 w-3 text-muted-foreground/60 group-hover:text-muted-foreground transition-colors"
                aria-hidden="true"
              />
            )}
          </div>
        </div>
        <div className="flex-1 min-h-0 min-w-0 cursor-default">{children}</div>
      </motion.div>
    </>
  );
};
